/**
 * SELISE Blocks v4 platform configuration.
 *
 * v4 renamed the v1 services and all `/…/v1/` routes are dead:
 *   idp                → iam            (`/iam/v4/…`)
 *   uds / data-gateway → data           (`/data/v4/…`)
 *   uilm               → localization   (`/localization/v4/…`)
 *
 * Every Blocks call carries `x-blocks-key: <project tenant id>`. Runtime calls
 * authenticate with the hosted SSO session cookie (`credentials: "include"`),
 * not a JS-held bearer token — see `./sso.ts`.
 */

const stripTrailingSlash = (url: string) => url.replace(/\/+$/, "");

/**
 * v4 API host. The SSO callback sets a Secure, domain-scoped cookie, so on web
 * this host MUST share a registrable domain with the app or the cookie is never
 * stored. `https://api.seliseblocks.com` only works for apps on
 * `*.seliseblocks.com`; a custom domain needs `https://blocksapi.<domain>`.
 *
 * Falls back to the legacy base URL so an unset override keeps the existing
 * host rather than silently pointing at the SaaS default.
 */
export const BLOCKS_API_URL = stripTrailingSlash(
  process.env.EXPO_PUBLIC_BLOCKS_API_URL ??
    process.env.EXPO_PUBLIC_BLOCKS_BASE_URL ??
    "",
);

/** Project tenant id — public, safe to ship in the client. */
export const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY ?? "";

/** OIDC client id from the project's `blocks-oidc` identity provider. */
export const OIDC_CLIENT_ID =
  process.env.EXPO_PUBLIC_BLOCKS_OIDC_CLIENT_ID ?? "";

/**
 * `clientId` of the **social** identity provider to log in with (the project's
 * Microsoft provider).
 *
 * This is now an **override, not the source of truth**: `/auth/login-options`
 * returns the provider name, its clientId and its registered `redirectUris` to a
 * signed-out caller (verified live 2026-08-02), so `sso.ts` discovers all three
 * at runtime and only falls back to this value if that call fails. An earlier
 * note here said discovery was impossible because `/auth/identity-providers` is
 * 401 signed out — true of that route, but `login-options` is the one the hosted
 * login page itself uses, and it needs no session.
 */
export const SOCIAL_CLIENT_ID =
  process.env.EXPO_PUBLIC_BLOCKS_SOCIAL_CLIENT_ID ?? "";

/**
 * Per-deployment override for where the provider sends the browser back with
 * `?code&state`. Normally unset: the registered `redirectUris` come back from
 * `/auth/login-options`, and `sso.ts` picks the entry matching the current
 * origin. Set this only to force one.
 *
 * ⚠ Whatever is used must ALSO be an allowed origin on the project. The Blocks
 * API answers `406 Invalid_Origin_Or_Referer` to a browser call from an
 * unlisted origin, so SSO cannot even start there — `https://app.selise.club`
 * is one such origin (measured 2026-08-02), which is why it is not the default.
 */
export const SSO_REDIRECT_URI_OVERRIDE =
  process.env.EXPO_PUBLIC_BLOCKS_SSO_REDIRECT_URI ?? "";

/** The override if set, else `/login` on the current origin. */
export const SSO_REDIRECT_URI = (): string => {
  if (SSO_REDIRECT_URI_OVERRIDE) return SSO_REDIRECT_URI_OVERRIDE;
  const origin = typeof window !== "undefined" ? window.location?.origin : "";
  return `${origin ?? ""}/login`;
};

export const IAM_BASE = `${BLOCKS_API_URL}/iam/v4`;
export const DATA_BASE = `${BLOCKS_API_URL}/data/v4`;
export const LOCALIZATION_BASE = `${BLOCKS_API_URL}/localization/v4`;

/** One GraphQL endpoint serves every schema in the project. */
export const GATEWAY_URL = `${DATA_BASE}/gateway`;

/**
 * v4 endpoint paths, verified against the blocks-skills contracts.
 * Note the repeated `/iam` segment on IAM management routes.
 */
export const BLOCKS_ENDPOINTS = {
  // SSO / session — blocks-iam-sso-oidc-implementation
  // Hosted `blocks-oidc` flow (Blocks' own login page). Kept because the
  // `selise-club-sso` provider still uses it, but see `socialInitiate` below for
  // the configured Microsoft provider.
  ssoInitiate: `${IAM_BASE}/idp/initiate`,

  // Social (federated) login — the flow for a provider whose
  // `providerType` is "social", which is what the project's Microsoft provider
  // is. Verified live 2026-07-30, UNAUTHENTICATED (login happens signed out):
  //
  //   GET /auth/social/initiate?clientId=<PROVIDER clientId>&redirectUri=<uri>
  //     → 200 {authorizationUrl}
  //
  // ⚠ `clientId` is the **identity provider's** clientId (Microsoft:
  // 4fb0573e-…), NOT the blocks-oidc client id. Passing the latter answers
  // 400 {"error":"invalid_provider_type","error_description":"Provider '…' is not
  // a social provider"}.
  //
  // ⚠ The returned `authorizationUrl` points at
  // `iam.seliseblocks.com/api/oidc/authorize`, which is NOT navigable by a
  // browser: it requires the `x-blocks-key` header and a top-level navigation
  // cannot send one (→ 404 Application_Not_Found). It 302s to
  // `iam.seliseblocks.com/oidc/login?<same query>`, which DOES load bare — so
  // `startSocialLogin` navigates to that form instead. See sso.ts.
  socialInitiate: `${IAM_BASE}/auth/social/initiate`,

  // Exchanges the `?code&state` the social provider redirected back with, for
  // the session cookie. Verified live 2026-08-02 — this is the route the hosted
  // login page itself calls (`SOCIAL_LOGIN` in its bundle):
  //
  //   POST /auth/social/callback  {code, state, provider}
  //
  // ⚠ The third field is the provider **name** ("microsoft"), NOT its clientId.
  // Sending `clientId` answers 400 `provider_missing` ("Provider name is
  // required"); sending `provider` gets as far as `state_data_not_found`, i.e.
  // the route resolved and looked the state up.
  //
  // ⚠ This is NOT `ssoCallback` below. `/idp/callback` and `/oidc/callback`
  // both reject a state minted by `social/initiate` — they belong to the hosted
  // `blocks-oidc` flow, and that mismatch is why the exchange had no home.
  socialCallback: `${IAM_BASE}/auth/social/callback`,

  // What the login screen can know about sign-in BEFORE there is a session.
  // Verified live 2026-08-02, unauthenticated → 200:
  //   {allowedGrantTypes, ssoInfo: [{provider, displayName, icon,
  //                                  clientId, redirectUris}]}
  // This is how the social provider's clientId and its registered redirect URIs
  // are discovered at runtime. Prefer it over `identityProviders`.
  loginOptions: `${IAM_BASE}/auth/login-options`,

  // The project's identity providers. ⚠ 401 without a session, so it cannot be
  // used to populate a signed-out login screen — use `loginOptions` above for
  // that. Returns both the Microsoft `social` provider and the `blocks-oidc`
  // one, with more detail than `loginOptions`, once signed in.
  identityProviders: `${IAM_BASE}/auth/identity-providers`,
  ssoCallback: `${IAM_BASE}/idp/callback`,
  oidcToken: `${IAM_BASE}/oidc/token`,
  logout: `${IAM_BASE}/auth/Logout`,
  activate: `${IAM_BASE}/auth/activate`,

  // Password flow — coexists with hosted SSO. A successful `authLogin` sets the
  // same HttpOnly session cookie the SSO callback does, so no JS-held token.
  //
  // This is `/auth/login` (slash), NOT `/auth-login` (dash). They are different
  // endpoints and only this one works for a project user — verified live
  // 2026-07-29 against the SELISE-CLUB project:
  //   /auth/login  → 200, Set-Cookie idp_session_id_<PROJECT tenant>, token
  //                  claims tenant_id = project key, aud = the app domain.
  //   /auth-login  → 500 "Object reference not set" on every variant (project
  //                  key, account key, no key). It is the account/platform
  //                  bootstrap login used by admin tooling — it ignores
  //                  x-blocks-key and scopes any session it does issue to the
  //                  user's HOME tenant, which is wrong for app sign-in.
  //
  // Bad credentials return a real 401 `{error: "invalid_username_password",
  // error_description, redirect_url}`. Captcha is enforced per tenant: if it is
  // enabled in the portal this answers 400 `{error: "captcha_enabled",
  // captcha_required, captcha_site_key}` and the form must send `captchaCode`.
  authLogin: `${IAM_BASE}/auth/login`,

  // Password recovery / activation. All three verified live 2026-07-30,
  // unauthenticated (they must work for a signed-out user):
  //   POST /auth/recover        {email}                    → 200 {itemId: null,
  //     isSuccess: true, errors: {}} — and the SAME 200 for an address that does
  //     not exist, i.e. it deliberately does not leak whether an account exists.
  //     Body must carry `email`: posting `{}` answers a 500 "An item with the
  //     same key has already been added. Key: Email".
  //   POST /auth/reset-password {code, password, logoutFromAllDevices}
  //     → 400 {errors: {Code: "The code has expired…"}} for a bogus code
  //   POST /auth/activate       {code, password}
  //     → 400 {errors: {Code: …}}; `{}` → "'Code' must not be empty."
  // `recover` replaces v1 `Iam/Recover`. NOTE it is NOT `/auth/forgot-password`:
  // that path answers 405 on POST and the IAM SPA on GET, which is exactly what
  // an absent route looks like here (`/auth/nonexistent-control-xyz` → 405 too).
  // This closes the "find forgot-password's real verb" open item.
  recover: `${IAM_BASE}/auth/recover`,
  resetPassword: `${IAM_BASE}/auth/reset-password`,
  changePassword: `${IAM_BASE}/auth/change-password`,

  // Session renewal. Verified 2026-07-29: this returns 200 and rotates the auth
  // cookies when called with an empty JSON body and the session cookies attached.
  // `oidcToken` below returns 401 in the same conditions — do NOT use it to renew
  // a cookie session.
  authRefresh: `${IAM_BASE}/auth/refresh`,

  // Active-organization switch — replaces the v1 form-encoded
  // `grant_type=switch_organization` password-grant call. Verified live
  // 2026-07-30 on an ordinary app session (no admin rights):
  //   {organization_id}          → 200, rotates the three session cookies so the
  //                                new access-token cookie carries the new org
  //   {}                         → 400 {error:"invalid_request",
  //                                "organization_id is required"}
  //   unknown org id             → 400 {error:"organization_not_available"}
  // Note snake_case `organization_id`. `/auth/switch-organization` is a 405 — the
  // hyphenated `switch-org` is the route. In browser mode (Origin present) the
  // 200 body carries no tokens, only the rotated cookies.
  switchOrg: `${IAM_BASE}/auth/switch-org`,

  // Resend a new user's activation email. `POST /iam/v4/auth/resend-activation`
  // — ✅ body CONFIRMED 2026-07-30 once the IAM grant landed: an empty body
  // answers `400 {"errors":{"UserId":"UserId_Required"}}`, so `{userId}` (the
  // shape carried over from v1) is correct. Absent from the skill docs entirely.
  //
  // Note the companion route is SELF-service: `POST /auth/activate` takes the
  // emailed `{code, password}` and needs no session (see `activate`). There is no
  // admin "activate this user" endpoint — blocks-iam-users documents only
  // deactivate and states reactivation goes through update/create or the portal.
  resendActivation: `${IAM_BASE}/auth/resend-activation`,

  // Users — blocks-iam-users
  //
  // ✅ These are READABLE since the project granted its roles IAM read permission
  // (2026-07-30). Re-verified live on an ordinary project-user cookie session:
  //   GET  /iam/me                     → 200 (`roles` flat array, `organizationId`)
  //   GET  /iam/users/{id}             → 200 (`roles` flat array, `organizationIds`)
  //   POST /iam/users                  → 200 {totalCount, data} — but `roles` comes
  //                                      back as an ORG-KEYED OBJECT, and there is
  //                                      no `permissions`/`memberships` field
  //   POST /iam/users/gets             → 400 {"errors":{"ItemId":"Not found"}} for
  //                                      VALID ids, incl. the caller's own
  //   GET  /iam/users/roles-and-permissions → 200 {data: null} for every param
  //                                      spelling tried (userId/UserId/itemId)
  //
  // ⚠ `userGets` is therefore NOT the single-user read its name suggests — use
  // `user(id)`. The earlier "single-user fetch despite the plural" note came from
  // an impersonated token's `400 "ItemId field is required"`, which only proved
  // the field was required.
  me: `${IAM_BASE}/iam/me`,
  // Same URL, POST instead of GET: the profile write. Verified 2026-07-30 —
  // 200 `{itemId, errors, isSuccess}` on an ordinary app session, no admin
  // rights needed. See `updateMe` in ./session.ts for its two sharp edges
  // (omitted fields are CLEARED, and the body's `itemId` is ignored).
  updateMe: `${IAM_BASE}/iam/me`,
  userGets: `${IAM_BASE}/iam/users/gets`,
  // Admin user-management — all POST, bodies from the local blocks-iam-users
  // skill docs. `users` (list/search) is verified 200; the mutating pair below is
  // reachable but was deliberately not probed by creating/deactivating a real
  // user, so their bodies remain doc-sourced rather than observed.
  users: `${IAM_BASE}/iam/users`,
  usersCreate: `${IAM_BASE}/iam/users/create`,
  usersDeactivate: `${IAM_BASE}/iam/users/deactivate`,
  // ✅ Admin activate — UNDOCUMENTED but real, found and used successfully
  // 2026-07-30. Absent from the blocks-iam-users skill, which lists only
  // deactivate and claims "reactivation / full lifecycle is managed via
  // update/create flows and the portal". It is not: this endpoint does it.
  //
  //   POST /iam/v4/iam/users/activate  {userId, reason}  → 200 {itemId, isSuccess}
  //
  // Both fields are REQUIRED — the empty-body 400 names the handler type,
  // `Iam.DomainService.Users.ActivateUserByAdminRequest`, "missing required
  // properties including: 'userId', 'reason'". `reason` is free text and is
  // presumably kept for the audit timeline, so pass something meaningful.
  // Verified end to end on a real inactive user: `active` false → true, and
  // `isVerified` flipped false → true as a side effect.
  //
  // Note `deactivate` takes `userId` while most sibling routes take `itemId`.
  usersActivate: `${IAM_BASE}/iam/users/activate`,
  user: (id: string) => `${IAM_BASE}/iam/users/${id}`,
  userRolesAndPermissions: `${IAM_BASE}/iam/users/roles-and-permissions`,

  // Access control (roles + permissions) — blocks-iam-access-control
  //
  // Their own prefixes, NOT `/iam/users/...`. ✅ Readable since the IAM grant
  // (2026-07-30); re-verified on an ordinary project-user cookie session:
  //   POST /iam/permissions  → 200, totalCount 146 — but `filter.search` AND
  //                            `filter.isBuiltIn` are REQUIRED, and `isBuiltIn`
  //                            is a STRING where only "" returns rows. See
  //                            `fetchBlocksPermissions` for the full trap list.
  //   POST /iam/roles        → 200, 2 roles (`clouduser` 146 perms, `admin` 0) —
  //                            `filter.search` REQUIRED here too.
  //   GET  /iam/roles/{itemId}     → 200. A SLUG returns `{data: null}`, so this
  //                            one is keyed by itemId even though assignment is
  //                            keyed by slug.
  //   GET  /iam/roles/assignable   → 200 {hierarchy, standalone}
  //   GET  /iam/permissions/by-severity → 200 (counts only)
  // The catalog is entirely Blocks-platform permissions (blocks-data,
  // blocks-release, localization, …) — the app's own names (`view-post`, …) do
  // NOT exist in it, so `usePermissions` matches nothing and every gate stays
  // open. Creating them is a portal action, not a code change.
  permissions: `${IAM_BASE}/iam/permissions`,
  roleCreate: `${IAM_BASE}/iam/roles/create`,
  roleAssignPermissions: `${IAM_BASE}/iam/roles/assign-permissions`,

  // Organizations — blocks-iam-organizations
  //
  // `organizations/my` → 200 on the session cookie alone (verified 2026-07-30:
  // `{organizations: [{itemId, name, createdDate}], errors, isSuccess}`).
  // The unscoped `organizations` list is 403 for an app session, same as the
  // user routes above — use `myOrganizations` from app code.
  myOrganizations: `${IAM_BASE}/iam/organizations/my`,
  organizations: `${IAM_BASE}/iam/organizations`,
  organization: (id: string) => `${IAM_BASE}/iam/organizations/${id}`,
  organizationConfig: `${IAM_BASE}/iam/organizations/config`,

  // Localization (UILM) — blocks-localization-implementation
  //
  // Verified live 2026-07-30, UNAUTHENTICATED, on both hosts: these are public
  // reads keyed by `x-blocks-key` alone (translations must load before login).
  //   GET /Language/Gets      → 200 [{itemId, languageName, languageCode,
  //                             isDefault}] — en-US (default), de-DE, bn-BD.
  //                             `ProjectKey` is optional; the header suffices.
  //   GET /Key/GetUilmFile?Language=&ModuleName=&ProjectKey=
  //                           → 200, a flat key→value map. Query params bind
  //                             case-insensitively (`language` works too).
  // Their v1 forms (`/uilm/v1/...`) 404 `Application_Not_Found` like the rest.
  //
  // Do NOT "correct" these to the `…Cloud…` swagger names (`GetCloudUilmFile`,
  // `GetCloudstLanguages`): those are impersonation-scoped and 401 with a project
  // key. Swagger omits these runtime read routes, as it does the data gateway.
  languages: `${LOCALIZATION_BASE}/Language/Gets`,
  uilmFile: `${LOCALIZATION_BASE}/Key/GetUilmFile`,
} as const;

/** Header required on every Blocks request. */
export const blocksHeaders = (): Record<string, string> => ({
  "x-blocks-key": PROJECT_KEY,
});

/**
 * True when the values the SSO flow needs are present. Surfaces a missing
 * `EXPO_PUBLIC_BLOCKS_OIDC_CLIENT_ID` as a config error instead of an opaque
 * 400 from `/idp/initiate`.
 */
export const isSsoConfigured = (): boolean =>
  !!BLOCKS_API_URL && !!PROJECT_KEY && !!OIDC_CLIENT_ID;
